import { Component, ElementRef, EventEmitter, Inject, LOCALE_ID, NgZone, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { ColDef, ColumnApi, GridApi, GridOptions } from 'ag-grid-community';
import moment from 'moment';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import {
  ApiService,
  AttachmentEntityType,
  CoreUiPartialState,
  getBrandDefaults,
  hasUnsavedChanges,
  isExternalUser,
  isProgrammeUser,
  MessageService,
  saveCustomerOrderDetailsForTask,
} from '@pos-app/core-ui';
import { CustomerQuoteOrderGridActionRenderComponent } from '../../../../orders/components/customerOrderGridActionrRenderer/customerQuoteOrderGridActionRenderer.component';
import { ParkedOrders } from '@pos-app/data';
import { CustomersPartialState } from '../../../+state/customers.reducer';
import { FetchJdeOrder } from '../../../../orders/+state/orders.actions';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ORDER_ATTACHMENT_TYPE } from '@pos-app/data';
import { getUserAssignedCurrencyFormat } from '@pos-app/core-ui/lib/utils/locale-helper';
import { ManageusersPartialState } from '../../../../manageusers/+state/manageusers.reducer';
import { ResetList } from '../../../../manageusers/+state/manageusers.actions';
import { Router } from '@angular/router';
import { ordersQuery } from '../../../../orders/+state/orders.selectors';
import { GetUserSalesPersonListAction } from '../../../+state/customers.actions';
import { customersQuery } from '../../../+state/customers.selectors';
import { CoreUiModule } from '../../../../../../../../../libs/core-ui/src/lib/core-ui.module';
import { FormsModule } from '@angular/forms';
import { AgGridModule } from 'ag-grid-angular';
import { NgIf, AsyncPipe } from '@angular/common';

@Component({
    selector: 'app-customers-quotes-orders',
    templateUrl: './customers-quotes-orders.component.html',
    styleUrls: ['./customers-quotes-orders.component.scss'],
    imports: [CoreUiModule, FormsModule, AgGridModule, NgIf, AsyncPipe]
})
export class CustomersQuotesOrdersComponent implements OnInit, OnDestroy {
  @ViewChild('content') modalBox: ElementRef;

  @Output() onFetchOrderFromJDE = new EventEmitter();

  gridOptions: GridOptions;
  columnDefs: ColDef[];
  frameworkComponents;
  context;
  quoteOrderList: any[];
  rowData = [];
  parkedOrderList: ParkedOrders[];
  searchValue: string;
  orderDetails: any;
  isReadOnlyLineAttachment: boolean;
  ORDER_ATTACHMENT_TYPE = ORDER_ATTACHMENT_TYPE;
  // legacy boolean replaced by observable for template binding
  public loading$ = new BehaviorSubject<boolean>(false);

  private unSubscribe$ = new Subject<void>();

  private gridApi: GridApi;
  private gridColumnApi: ColumnApi;
  // (kept for compatibility): loading$ already declared above
  currencyCode: string;
  public isExternalUser$: Observable<boolean>;
  public isProgrammeUser$: Observable<boolean>;

  show = false;
  formValid: boolean = false;
  internalUser: boolean = false;
  salesPeopleList: any;
  selectedSalesPerson: string;
  selectedSalesPersonEmail: string;
  allAttachments: any;
  entityId: string;
  entityType: AttachmentEntityType;
  showAttachment: boolean = false;
  madeChanges: any;
  defaultCompanyCode: string = JSON.parse(localStorage.getItem('jdeSession')).CompanyCode;
  public loadingFetchingOrderFromJDE$: Observable<boolean>;
  isShowOrderAttachment: boolean;
  isShowSalesPersonSelector: boolean;
  constructor(
    private apiService: ApiService,
    private customerStore: Store<CustomersPartialState>,
    private toastr: ToastrService,
    private modalService: NgbModal,
    private coreUiStore: Store<CoreUiPartialState>,
    private store: Store<ManageusersPartialState>,
    private router: Router,
    private ngZone: NgZone,
    private messageService: MessageService,

    @Inject(LOCALE_ID) public locale: string
  ) {
    this.frameworkComponents = {
      actionRenderer: CustomerQuoteOrderGridActionRenderComponent,
    };
    this.context = { componentParent: this };
    this.gridOptions = this.gridOptionsData();
  }

  ngOnInit(): void {
    this.coreUiStore
      .select(getBrandDefaults)
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((data) => {
        if (data) {
          this.currencyCode = data?.currrencyCode;
          this.fetchOpenQuotesOrders();
        }
      });
    this.isExternalUser$ = this.coreUiStore.select(isExternalUser);
    this.isProgrammeUser$ = this.coreUiStore.select(isProgrammeUser);
    this.isExternalUser$.pipe(takeUntil(this.unSubscribe$)).subscribe((isExternalUser) => {
      this.isProgrammeUser$.pipe(takeUntil(this.unSubscribe$)).subscribe((isProgrammeUser) => {
        if (isExternalUser || isProgrammeUser) {
          this.internalUser = false;
        } else {
          this.internalUser = true;
        }
      });
    });
    this.loadingFetchingOrderFromJDE$ = this.customerStore.select(ordersQuery.getIsFetchingJdeOrder);
    this.customerStore.dispatch(new GetUserSalesPersonListAction());
  }

  ngOnDestroy() {
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
  }

  gridOptionsData() {
    return {
      defaultColDef: {
        resizable: true,
        sortable: true,
        width: 150,
        cellClass: 'cell-class ag-right-aligned-cell',
        headerClass: 'header-class ag-left-aligned-header',
      },
      columnDefs: [
        {
          headerName: 'ACTION',
          width: 150,
          cellRenderer: 'actionRenderer',
          cellStyle: { 'text-align': 'left' },
        },
        {
          headerName: 'QUOTE NO',
          width: 150,
          field: 'orderNumber',
          cellRenderer: (params) => {
            const link = document.createElement('a');
            link.href = 'javascript:void(0)';
            link.innerText = params.value;
            link.addEventListener('click', () => {
              this.fetchOrderFromJDE(params.data);
            });
            return link;
          },
          cellStyle: { 'text-align': 'left' },
        },
        {
          headerName: 'SALESPERSON',
          width: 150,
          field: 'salesperson',
          cellStyle: { 'text-align': 'left' },
        },
        {
          headerName: 'REFERENCE',
          width: 200,
          field: 'reference',
          cellStyle: { 'text-align': 'left' },
        },

        {
          headerName: 'CUSTOMER NAME',
          width: 200,
          field: 'customerName',
          cellStyle: { 'text-align': 'left' },
        },
        {
          headerName: 'PHONE',
          width: 150,
          field: 'phone',
          cellStyle: { 'text-align': 'left' },
        },
        {
          headerName: 'QUOTE VALUE',
          width: 150,
          field: 'totalPrice',
          cellRenderer: (params) => {
            return this.displayPrice(params.value, params.data.currencyCode);
          },
          cellStyle: { 'text-align': 'left' },
        },
        {
          headerName: 'CREATED DATE',
          width: 180,
          field: 'orderDate',
          comparator: this.dateComparator,
          cellStyle: { 'text-align': 'left' },
        },
        {
          headerName: 'REQUEST DATE',
          width: 180,
          field: 'requestedDate',
          comparator: this.dateComparator,
          cellStyle: { 'text-align': 'left' },
        },
      ],
      onGridReady: (params) => {
        params.columnApi.applyColumnState({
          state: [{ colId: 'requestedDate', sort: 'asc' }],
          applyOrder: true,
        });
      },
    };
  }

  dateComparator(date1: string, date2: string): number {
    const dateParts1 = date1.split('/').map((part) => parseInt(part, 10));
    const dateParts2 = date2.split('/').map((part) => parseInt(part, 10));
    const d1 = new Date(dateParts1[2], dateParts1[1] - 1, dateParts1[0]);
    const d2 = new Date(dateParts2[2], dateParts2[1] - 1, dateParts2[0]);
    return d1.getTime() - d2.getTime();
  }

  dateFormatter(params) {
    return moment(params.value).format('L <HH:mm>');
  }

  fetchOpenQuotesOrders() {
    this.loading$.next(true);
    this.apiService
      .fetchOpenQuotesOrders()
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe({
        next: (res) => {
          res.quoteList.forEach((quote) => {
            quote.internalUser = this.internalUser;
          });
          this.rowData = res.quoteList;
          this.loading$.next(false);
        },
        error: (error) => {
          this.loading$.next(false);
        },
        complete: () => {
          this.loading$.next(false);
        },
      });

    if (!!this.rowData) {
      this.gridApi?.setRowData(this.rowData);
    }
  }

  onGridReady(params) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
    const sortModel = [{ colId: 'dateTime', sort: 'desc' }];
  }

  fetchOrderFromJDE(data) {
    //
    this.customerStore.dispatch(
      new FetchJdeOrder({
        orderNumber: data.orderNumber,
        orderType: data.orderType,
        JDEOrderType: data?.JDEOrderType,
        JDECompany: data?.JDECompany,
      })
    );
  }

  updateRequestDate(newDate: string, data: any) {
    if (newDate === data.requestedDate) {
      return;
    }
    const [day, month, year] = newDate.split('/').map(Number);
    const dateObject = new Date(year, month - 1, day);

    // Get today's date at midnight
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (dateObject >= today) {
      const dataHeader = {
        requestDate: newDate,
        orderNumber: data.orderNumber,
        JDEOrderType: data?.JDEOrderType,
        JDECompany: data?.JDECompany,
      };

      this.apiService
        .updateRequestDateOfSavedOrder(dataHeader)
        .pipe(takeUntil(this.unSubscribe$))
        .subscribe({
          next: (res) => {
            if (res) {
              this.toastr.success('Request date updated successfully');
              this.fetchOpenQuotesOrders();
            }
          },
          error: (error) => {
            this.toastr.error(error?.ErrorMessage || 'An error occurred while updating the request date');
          },
        });
    } else {
      this.toastr.error("Request date cannot be earlier than today's date");
    }
  }

  attachedItem(cell, isReadOnly) {
    this.entityId = `${cell.orderNumber.toString()}|${cell.orderType}|${this.defaultCompanyCode}`;
    this.entityType = 'order';
    this.orderDetails = cell;
    this.isReadOnlyLineAttachment = isReadOnly;
    this.isShowOrderAttachment = true;
    this.loading$.next(true);
    this.apiService
      .loadAttachmentForOrder({
        orderNumber: cell.orderNumber,
        orderType: cell.orderType,
        JDEOrderType: cell?.JDEOrderType,
        JDECompany: cell?.JDECompany,
      })
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((res: any) => {
        this.loading$.next(false)
        this.allAttachments = res?.attachmentIDArray;
        this.openModalBox();
      });
  }

  loadAllAttachments(attachments: any) {
    this.madeChanges = true;
    this.allAttachments = attachments;
  }

  formatTime(time) {
    // JDE time doesn't put a 0 at the start when less than 10am
    if (parseInt(time.slice(0, 2)) > 24) {
      return ['0' + time.slice(0, 1), ':', time.slice(1, 3)].join('');
    } else {
      return [time.slice(0, 2), ':', time.slice(2, 4)].join('');
    }
  }

  downloadFile(item) {
    this.apiService.downloadFile(item._id, item.name).subscribe((x) => {});
  }

  closeModal() {
    this.formValid = false;
    this.modalService.dismissAll();
  }

  displayPrice(price: number, currencyCode: string = ''): string {
    if (!price) {
      return '';
    }
    if (currencyCode !== '') {
      this.currencyCode = currencyCode;
    }

    return getUserAssignedCurrencyFormat(price, this.locale, this.currencyCode);
  }
  changeSalesPerson(cell) {
    this.orderDetails = cell;
    this.selectedSalesPerson = cell?.salesperson;
    this.customerStore
      .select(customersQuery.getUserSalespersonList)
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((data) => {
        if (data) {
          this.salesPeopleList = data;
          if (this.salesPeopleList?.length > 0) {
            this.isShowSalesPersonSelector = true;
            this.openModalBox();
          } else {
            this.toastr.error('No salesperson list found');
          }
        }
      });
  }

  handleSelectedUser(user) {
    this.selectedSalesPerson = user?.userEmail;
    if (this.selectedSalesPerson && user?.name !== this.orderDetails?.salesperson) {
      this.formValid = true;
    } else {
      this.formValid = false;
    }
  }

  updateSalsePerson() {
    this.apiService
      .updateOwnerOfSavedOrder({
        orderNumber: this.orderDetails?.orderNumber,
        JDEOrderType: this.orderDetails?.JDEOrderType,
        JDECompany: this.orderDetails?.JDECompany,
        newOwner: this.selectedSalesPerson,
      })
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((res) => {
        if ((res.errorFlag = '0')) {
          this.toastr.success('Salesperson updated successfully');
        } else {
          this.toastr.error(res?.errorMessage);
        }
        this.fetchOpenQuotesOrders();
        this.selectedSalesPerson = null;
        this.store.dispatch(new ResetList());
        this.modalService.dismissAll();
      });
  }

  loadTask(data) {
    this.router.navigate([`central/task-search-order/${data.orderNumber}/${data.orderType}`]);
  }

  getServiceAttachmentEntryId(assetNumber: string, serviceDate: string) {
    return `${assetNumber}|${this.yearMonthDayDateFormat(serviceDate)}`;
  }

  yearMonthDayDateFormat(date: string) {
    let formatedDate = date.split('/');
    return formatedDate[2] + formatedDate[1] + formatedDate[0];
  }

  // handleCloseModal(event) {
  //   if (this.showAttachment) {
  //     if (this.madeChanges) {
  //       this.loading = true;
  //       this.modalService.dismissAll();
  //       this.apiService
  //         .saveAttachmentForOrder({
  //           orderNumber: this.orderDetails.orderNumber,
  //           orderType: this.orderDetails.orderType,
  //           JDEOrderType: this.orderDetails.JDEOrderType,
  //           JDECompany: this.orderDetails.JDECompany,
  //         })
  //         .pipe(takeUntil(this.unSubscribe$))
  //         .subscribe((res: any) => {
  //           this.loading = false;
  //           this.allAttachments = res?.attachmentIDArray;
  //           this.madeChanges = false;
  //           if (event) {
  //             this.showAttachment = false;
  //           }
  //         });
  //     } else {
  //       this.loading = true;
  //       this.modalService.dismissAll();
  //       this.apiService
  //         .unloadAttachmentForOrder({
  //           orderNumber: this.orderDetails.orderNumber,
  //           orderType: this.orderDetails.orderType,
  //         })
  //         .pipe(takeUntil(this.unSubscribe$))
  //         .subscribe((res: any) => {
  //           this.loading = false;
  //           this.allAttachments = res?.attachmentIDArray;
  //           this.showAttachment = false;
  //           if (event) {
  //             this.showAttachment = false;
  //           }
  //         });
  //     }
  //   }
  // }
  addNewTaskForOrder(data) {
    this.orderDetails = data;
    this.coreUiStore.dispatch(saveCustomerOrderDetailsForTask({ customerOrderDetails: { ...data, newTask: true } }));
    this.router.navigate([`central/task/new`]);
  }
  openModalBox() {
    this.modalService
      .open(this.modalBox, {
        windowClass: 'garage-modal',
        size: 'xl',
        backdrop: 'static',
      })
      .result.then(
        (_) => {
          this.isShowOrderAttachment = false;
          this.isShowSalesPersonSelector = false;
        },
        (_) => {
          this.isShowOrderAttachment = false;
          this.isShowSalesPersonSelector = false;
        }
      );
  }
  handleCloseAttachmentModel(event) {
    if (this.isShowOrderAttachment) {
      if (this.madeChanges) {
        this.loading$.next(true);
        this.modalService.dismissAll();
        this.apiService
          .saveAttachmentForOrder({
            orderNumber: this.orderDetails.orderNumber,
            orderType: this.orderDetails.orderType,
            JDEOrderType: this.orderDetails.JDEOrderType,
            JDECompany: this.orderDetails.JDECompany,
          })
          .pipe(takeUntil(this.unSubscribe$))
          .subscribe((res: any) => {
            this.loading$.next(false);
            this.allAttachments = res?.attachmentIDArray;
            this.madeChanges = false;
            if (event) {
              this.isShowOrderAttachment = false;
              this.fetchOpenQuotesOrders();
            }
          });
      } else {
        this.loading$.next(true);
        this.modalService.dismissAll();
        this.apiService
          .unloadAttachmentForOrder({
            orderNumber: this.orderDetails.orderNumber,
            orderType: this.orderDetails.orderType,
          })
          .pipe(takeUntil(this.unSubscribe$))
          .subscribe((res: any) => {
            this.loading$.next(false);
            this.allAttachments = res?.attachmentIDArray;
            this.isShowOrderAttachment = false;
            if (event) {
              this.isShowOrderAttachment = false;
            }
          });
      }
    }
  }

  reloadData() {
    this.fetchOpenQuotesOrders();
    this.selectedSalesPerson = null;
    this.modalService.dismissAll();
  }

  saveTask(objectToUpdate) {
    this.apiService.maintainTask(objectToUpdate).subscribe((res) => {
      if (res?.ErrorFlag === '0') {
        this.toastr.success('Task Updated Successfully');
        this.formValid = false;
        this.store.dispatch(hasUnsavedChanges({ unsavedChanges: false }));
        this.messageService.dispatchAction(hasUnsavedChanges({ unsavedChanges: false }));
        this.modalService.dismissAll();
        this.fetchOpenQuotesOrders();
      }
    });
  }

  exportGrid() {
    if (this.gridApi) {
      this.gridApi.exportDataAsCsv({
        fileName: `QuotesOrders_${moment().format('YYYYMMDD_HHmmss')}.csv`,
        columnKeys: ['orderNumber', 'salesperson', 'reference', 'customerName', 'phone', 'totalPrice', 'orderDate', 'requestedDate'],
        processCellCallback: (params) => {
          if (params.column.getColId() === 'totalPrice') {
            return this.displayPrice(params.value);
          }
          return params.value;
        },
      });
    }
  }
}

